import { FormBuilder } from '@/components/formBuilder';
import React, { useState, useEffect } from 'react';
import { SchedulerDetailsConstants } from '../SchedulerDetails.constants';
import { useInterviewSchedulerFormConfig } from './useInterviewSchedulerFormConfig';
import moment from 'moment-timezone';
import { useAuth, useDateFormat } from '@/hooks';
import { useQuery } from '@tanstack/react-query';
import { InterviewDetailsAPI } from '../api/InterviewDetailsAPI';
import { useKFlags } from '@/hooks/use-k-flags';

const getDurationFromTemplate = (obj?: {
  start_time?: string;
  end_time?: string;
  duration?: number;
}) => {
  const { start_time, end_time, duration } = obj || {};
  if (!start_time || !end_time)
    return duration || SchedulerDetailsConstants.DURATION;
  return moment(end_time).diff(moment(start_time), 'minutes');
};

export const InterviewSchedulerForm = ({
  calendarRef,
  interviewDataRef,
  updateSharedData,
  registerForUpdates,
  interviewPlan,
  jobStageActivityId,
  formRef,
  isEdit,
  isAdhoc,
  variant,
  isBulkManual,
  applicationData,
  currentTemplateData,
  updateTemplateModificationData,

  customForm = null,
  interviewPlatform = null,
  showScorecardStep = false,
}) => {
  const { data: { locations: locationOptions = {} } = {} } = useQuery({
    queryKey: ['scheduleInterviewLocations'],
    queryFn: async () => {
      const response = await InterviewDetailsAPI.getInterviewLocations();
      return response;
    },
  });

  const { feSchedulingPreference, feDisableAiNotetaker } = useKFlags();
  const { currUser } = useAuth();

  const { formConfig } = useInterviewSchedulerFormConfig({
    calendarRef,
    locationOptions,
    interviewDataRef,
    updateSharedData,
    registerForUpdates,
    interviewPlan,
    jobStageActivityId,
    formRef,
    variant,
    isAdhoc,
    isEdit,
    isBulkManual,
    applicationData,
    feSchedulingPreference,
    currUser,
    currentTemplateData,
    updateTemplateModificationData,
    interviewPlatform,
    showScorecardStep,
  });

  const [initialValues, setInitialValues] = useState({});
  const { dateFormat } = useDateFormat();

  useEffect(() => {
    const {
      name,
      substituted_name,
      host,
      participants,
      interviewers,
      interviewersDetails: interviewersDetailsFromPlan,
      note_taker_enabled,
      fill_with_ai_enabled,
      meeting_rooms,
      note_taker_mandatory,
      ats_office,
      shared_calendar_enabled,
      ext_interview_template_id,
      hackerrank_interview,
    } = interviewPlan ?? {};

    const isSelfSchedule = variant === 'self';

    let interviewersValue;
    let interviewersDetails;

    const isUnManagedKind =
      (interviewPlan?.kind || interviewPlan?.interview_kind) === 'unmanaged';

    let interviewKind =
      interviewPlan?.kind ||
      interviewPlan?.interview_kind ||
      (isAdhoc ? 'one_on_one' : 'panel');

    // Determine interview kind based on unmanaged status and participant count
    if (isUnManagedKind) {
      if (interviewPlan?.participants?.length > 1) {
        interviewKind = 'panel';
      } else {
        interviewKind = 'one_on_one';
      }
    }

    // Set interviewersValue based on edit, self-schedule, or manual schedule logic
    if (isEdit) {
      const ids = participants?.map((item) => item?.id);
      if (interviewKind === 'one_on_one' && variant === 'manual') {
        interviewersValue = ids?.[0]; // Single interviewer for one-on-one in edit mode
      } else if (['one_on_one', 'panel'].includes(interviewKind)) {
        interviewersValue = ids; // Multiple interviewers for panel and one on one in edit mode
      }

      // In edit mode, use the participants directly
      interviewersDetails = participants;
    } else if (isBulkManual) {
      interviewersValue = interviewersDetailsFromPlan?.map((i) => i?.id);
      // this comes only on bulk manual
      interviewersDetails = interviewersDetailsFromPlan;
    } else if (isSelfSchedule) {
      interviewersValue = interviewers?.map((i) => i?.id); // Load all interviewers for self-schedule

      // For self-schedule, map from interviewers data if available
      interviewersDetails = interviewers?.map((interviewer) => {
        return {
          ...interviewer,
          id: interviewer?.id,
          name: interviewer?.name,
          email: interviewer?.email,
        };
      });
    } else {
      if (interviewKind === 'one_on_one') {
        const isSingleInterviewer = interviewers?.length === 1;
        interviewersValue = isSingleInterviewer
          ? interviewers[0]?.id
          : undefined; // Pre-load single interviewer if available

        // For one-on-one in manual schedule
        if (isSingleInterviewer) {
          interviewersDetails = [
            {
              ...interviewers?.[0],
              id: interviewers[0]?.id,
              name: interviewers[0]?.name,
              email: interviewers[0]?.email,
            },
          ];
        } else {
          // No interviewer pre-selected
          interviewersDetails = undefined;
        }
      } else if (interviewKind === 'panel') {
        interviewersValue = interviewers?.map((i) => i?.id); // Load all interviewers for panel

        // For panel interviews, map all interviewers to participants
        interviewersDetails = interviewers?.map((interviewer) => {
          return {
            ...interviewer,
            id: interviewer?.id,
            name: interviewer?.name,
            email: interviewer?.email,
          };
        });
      }
    }

    // Determine initial location with integration availability check
    // Priority: Plan location (if set & available) > Zoom > Google Meet > Teams > Phone
    const initialLocation = (() => {
      if (interviewPlatform === 'hackerrank') {
        return 'hackerrank';
      }

      const planLocation =
        interviewPlan?.location === 'unspecified' || !interviewPlan?.location
          ? null
          : interviewPlan.location;

      // If the plan explicitly set a location and it's available, respect it
      if (planLocation && locationOptions?.[planLocation]) {
        return planLocation;
      }

      // Adhoc (no plan location) or plan location unavailable:
      // Zoom > Google Meet > Teams > Phone Interview
      // Note: if Zoom is later found disabled (no eligible host), SchLocation
      // will automatically fall back to the next available option.
      if (locationOptions?.zoom) return 'zoom';
      if (locationOptions?.google_meet) return 'google_meet';
      if (locationOptions?.teams) return 'teams';
      return 'phone_interview';
    })();

    const start = interviewPlan?.start_time
      ? moment(interviewPlan?.start_time)
      : null;
    const end = interviewPlan?.end_time
      ? moment(interviewPlan?.end_time)
      : null;

    const hasBookedSlot = start && end;

    const manualScheduleInfos = hasBookedSlot
      ? {
          startDate: start.format(dateFormat?.label),
          endDate: end.format(dateFormat?.label),
          startTime: start.format('hh:mm A'),
          endTime: end.format('hh:mm A'),
        }
      : {};

    // for adhoc interview, we don't need to show the name - for a customer ask
    const interviewName =
      isAdhoc && !isEdit ? '' : substituted_name || name || '';

    // Determine hostId based on location, edit mode, and user's ability to host
    let hostId = undefined; // Default to no host or host not applicable
    if (initialLocation === 'zoom') {
      if (isEdit) {
        // If editing an existing interview with Zoom, use the saved host
        hostId = host?.id;
      } else if (interviewPlan?.can_host_zoom_meeting) {
        // If creating a new interview with Zoom and current user can host,
        // set current user as host.
        hostId = currUser?.id;
      }
      // If initialLocation is 'zoom', but it's a new interview and the current user
      // cannot host (else if condition is false), hostId remains undefined.
    }
    // If initialLocation is not 'zoom', hostId remains undefined as initialized.

    // Set initialValues with all form fields
    // For bulk mode, if user has made a calendar selection, default to calendar view
    const showDisplayMode =
      isBulkManual &&
      (interviewPlan?.calendar_info?.user_selected ||
        interviewDataRef?.current?.calendar_info?.user_selected)
        ? 'calendar'
        : 'info';
    let computedNoteTakerEnabled;
    if (isEdit || interviewPlan?.job_stage_action_id) {
      // In edit mode, always preserve the existing note_taker_enabled value
      computedNoteTakerEnabled = Boolean(note_taker_enabled);
    } else if (feDisableAiNotetaker && !note_taker_mandatory) {
      // Feature flag disabled and not mandatory
      computedNoteTakerEnabled = false;
    } else {
      // Default: enabled if mandatory or already enabled
      computedNoteTakerEnabled =
        Boolean(note_taker_mandatory) || Boolean(note_taker_enabled);
    }

    let computedFillWithAiEnabled: boolean;
    if (isEdit || interviewPlan?.job_stage_action_id) {
      // In edit mode, always preserve the existing fill_with_ai_enabled value
      computedFillWithAiEnabled = Boolean(fill_with_ai_enabled);
    } else {
      // Default: enabled if note taker is mandatory or plan already has it enabled
      computedFillWithAiEnabled =
        Boolean(note_taker_mandatory) || Boolean(fill_with_ai_enabled);
    }

    const computedInitialValues = {
      ...manualScheduleInfos,
      showDisplayMode: showDisplayMode,
      name: interviewName,
      host_id: hostId,
      kind: interviewKind,
      note_taker_enabled: computedNoteTakerEnabled,
      note_taker_mandatory: Boolean(note_taker_mandatory),
      fill_with_ai_enabled: computedFillWithAiEnabled,
      interviewers: interviewersValue,
      shared_calendar_enabled: shared_calendar_enabled,
      meeting_rooms_enabled: meeting_rooms?.length > 0,
      meeting_room_ids: isBulkManual
        ? meeting_rooms // we will get the meeting rooms emails from the plan for bulk case
        : meeting_rooms?.map((room) => room?.email) || [],
      ats_office_id: ats_office?.id,
      interviewersDetails,
      ats_office,
      duration:
        variant === 'manual' || isBulkManual
          ? getDurationFromTemplate(interviewPlan)
          : interviewPlan?.duration || SchedulerDetailsConstants.DURATION,
      location: initialLocation,
      ext_interview_template_id:
        hackerrank_interview?.ext_interview_template_id ||
        ext_interview_template_id ||
        null,
    };

    updateSharedData({
      interviewersDetails,
      note_taker_enabled: computedNoteTakerEnabled,
      note_taker_mandatory: Boolean(note_taker_mandatory),
      interviewLocation: initialLocation,
      hostId: hostId,
    });

    setInitialValues(computedInitialValues);
  }, [
    updateSharedData,
    dateFormat?.label,
    interviewDataRef,
    interviewPlan,
    isEdit,
    variant,
    currUser,
    isAdhoc,
    locationOptions,
    isBulkManual,
    interviewPlatform,
    feDisableAiNotetaker,
  ]);

  // If customForm is provided, render it instead of the default form
  if (customForm) {
    // Refs are only read in effects/callbacks in the custom form, not during render
    // eslint-disable-next-line react-hooks/refs -- passing refs as props for cloneElement
    return React.cloneElement(customForm, {
      updateSharedData,
      interviewDataRef,
      registerForUpdates,
      calendarRef,
    });
  }

  return (
    <FormBuilder
      initialValues={initialValues}
      formRef={formRef}
      config={[...formConfig]}
    />
  );
};
